/**
 * AdminEinstellungenPage — systemweite Einstellungen (WP6).
 *
 * Eigene Seite nach dem UI-Muster der EinstellungenPage (Sektions-Nav links,
 * Inhalt rechts). Bündelt alle organisationsweiten Sektionen, die zuvor in
 * der persönlichen Einstellungen-Seite lagen:
 *
 *   - Organisation      (Firmenname + Zeitzone + Währung via
 *                        GET/PUT /v2/admin/system-settings)
 *   - E-Mail-Vorlagen   (<EmailTemplatesSettings/>)
 *   - PB Stages         (<PhasenSettings/>)
 *   - BK Stages         (<BkStagesSettings/> + <BkPauseSetting/> — Pause
 *                        nach Betreuung in Tagen, Default 21)
 *   - Matching          (<MatchingSettings/> — Gewichte der Vergleichsfelder,
 *                        Kandidaten je Liste; GET/PUT /v2/admin/matching-settings)
 *   - Kalender          (<KalenderSettings/>)
 *   - Export            (<ExportSettings/> — Masterliste, portiert aus CRM 1.0)
 *   - DocuSign          (<DocusignSettings/> — Vorlagen des verbundenen
 *                        DocuSign-Kontos inkl. Rollen und Datenfeldern)
 *   - Rollen & Rechte   (<RollenPage/>)
 *   - Sonstiges         (Leadquellen-Katalog <LeadquellenSettings/> — Sammel-
 *                        sektion für organisationsweite Stammdaten, die in
 *                        keine der fachlichen Sektionen oben gehören)
 *
 * Zugriff ist über die Permission `menu.admin_einstellungen` gated — ohne sie
 * wird eine "Kein Zugriff"-Karte gerendert (die Sidebar blendet den
 * Menüpunkt zusätzlich aus).
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Button, Field, Input, SectionTitle } from '../../components/ui';
import { ProtokollTable } from '../protokoll/ProtokollTable';
import { Icon } from '../../components/icons';
import { BRAND, BRAND_LIGHT, getThemeColors, DENSITY_VALUES } from '../../lib/theme';
import { useIsMobile } from '../../lib/useBreakpoint';
import { usePermissions } from '../../contexts/PermissionsContext';
import { systemSettingsApi, type SystemSettings } from '../../lib/api';
import { EmailTemplatesSettings } from './EmailTemplatesSettings';
import { PhasenSettings } from './PhasenSettings';
import { BkStagesSettings } from './BkStagesSettings';
import { AbbruchGruendeSettings } from './AbbruchGruendeSettings';
import { LeadquellenSettings } from './LeadquellenSettings';
import { WiedervorlageMaxSetting } from './WiedervorlageMaxSetting';
import { BkPauseSetting } from './BkPauseSetting';
import { MatchingSettings } from './MatchingSettings';
import { KalenderSettings } from './KalenderSettings';
import { ExportSettings } from './ExportSettings';
import { DocusignSettings } from './DocusignSettings';
import { ApiSettings } from './ApiSettings';
import { RollenPage } from '../rollen/RollenPage';
import type { Density, ThemeColors } from '../../types';

interface AdminEinstellungenPageProps {
  dark: boolean;
  density: Density;
}

type Section = 'organisation' | 'email' | 'pb_stages' | 'bk_stages' | 'abbruch_gruende' | 'matching' | 'kalender' | 'export' | 'docusign' | 'api' | 'protokoll' | 'rollen' | 'sonstiges';

const SECTIONS: { k: Section; labelKey: string; ic: keyof typeof Icon; permission?: string }[] = [
  { k: 'organisation', labelKey: 'settings.general.title',        ic: 'Settings' },
  { k: 'email',        labelKey: 'settings.nav.email_templates',  ic: 'Mail' },
  { k: 'pb_stages',    labelKey: 'settings.nav.phases',           ic: 'Heart' },
  { k: 'bk_stages',    labelKey: 'settings.bk_stages.title',      ic: 'Users' },
  { k: 'abbruch_gruende', labelKey: 'settings.abbruch_gruende.title', ic: 'Close' },
  // Matching — Gewichte der Vergleichsfelder, von Admins selbst einstellbar.
  { k: 'matching',     labelKey: 'settings.nav.matching',         ic: 'Heart' },
  { k: 'kalender',     labelKey: 'settings.nav.calendar',         ic: 'Calendar' },
  { k: 'export',       labelKey: 'settings.nav.export',           ic: 'Download' },
  // DocuSign — Vorlagen des verbundenen Kontos (FA-010, nur lesend).
  { k: 'docusign',     labelKey: 'settings.nav.docusign',         ic: 'File' },
  // API-Einstellungen (KI-Übersetzung TP1): KI-Anbieter + Übersetzungs-Einstellungen.
  { k: 'api',          labelKey: 'settings.nav.api',              ic: 'Settings' },
  // Aktionsprotokoll (activity_protocol): nur mit protokoll.read sichtbar.
  { k: 'protokoll',    labelKey: 'settings.nav.protokoll',        ic: 'List', permission: 'protokoll.read' },
  { k: 'rollen',       labelKey: 'settings.nav.roles',            ic: 'Shield' },
  // Sammelsektion — nimmt organisationsweite Kataloge auf, die keine eigene
  // fachliche Sektion rechtfertigen (aktuell: Leadquellen).
  { k: 'sonstiges',    labelKey: 'settings.nav.sonstiges',        ic: 'List' },
];

/** Sinnvolle IANA-Zeitzonen für den Einsatzraum DE/PL/CH + Fallbacks. */
const TIMEZONES = [
  'Europe/Berlin',
  'Europe/Vienna',
  'Europe/Zurich',
  'Europe/Warsaw',
  'Europe/Amsterdam',
  'Europe/Brussels',
  'Europe/Paris',
  'Europe/Prague',
  'Europe/Madrid',
  'Europe/Rome',
  'Europe/London',
  'Europe/Bucharest',
  'Europe/Kyiv',
  'UTC',
];

const CURRENCIES = [
  { code: 'EUR', label: 'EUR (€)' },
  { code: 'PLN', label: 'PLN (zł)' },
  { code: 'CHF', label: 'CHF (Fr.)' },
  { code: 'USD', label: 'USD ($)' },
];

function selectStyle(t: ThemeColors): React.CSSProperties {
  return {
    width: '100%', border: `1px solid ${t.border}`, borderRadius: 9,
    padding: '9px 12px', fontSize: 13, color: t.heading, background: t.inputBg,
    fontFamily: 'Outfit, sans-serif', cursor: 'pointer', outline: 'none',
  };
}

/* ── Organisation: Firmenname + Zeitzone + Währung (system-settings) ──────── */

function OrganisationSection({ dark, isMobile, cardPad }: { dark: boolean; isMobile: boolean; cardPad: number }) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveState, setSaveState] = useState<'idle' | 'success' | 'error'>('idle');

  const [companyName, setCompanyName] = useState('');
  const [timezone, setTimezone] = useState('Europe/Berlin');
  const [currency, setCurrency] = useState('EUR');

  const load = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const s: SystemSettings = await systemSettingsApi.get();
      setCompanyName(s.company_name ?? '');
      setTimezone(s.timezone || 'Europe/Berlin');
      setCurrency(s.currency || 'EUR');
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    setSaving(true);
    setSaveState('idle');
    try {
      await systemSettingsApi.update({ company_name: companyName, timezone, currency });
      setSaveState('success');
    } catch {
      setSaveState('error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card dark={dark} style={{ padding: cardPad }}>
        <div style={{ color: t.muted, fontSize: 13 }}>{tr('admin_settings.organisation.loading')}</div>
      </Card>
    );
  }

  if (loadError) {
    return (
      <Card dark={dark} style={{ padding: cardPad }}>
        <div style={{ color: '#b42318', fontSize: 13, marginBottom: 12 }}>
          {tr('admin_settings.organisation.load_error')}
        </div>
        <Button variant="secondary" size="sm" dark={dark} onClick={() => void load()}>
          {tr('admin_settings.organisation.retry')}
        </Button>
      </Card>
    );
  }

  // Aktuelle Zeitzone immer anbieten, auch wenn sie nicht in der kuratierten Liste steht
  const tzOptions = TIMEZONES.includes(timezone) ? TIMEZONES : [timezone, ...TIMEZONES];

  return (
    <Card dark={dark} style={{ padding: cardPad }}>
      <SectionTitle dark={dark} title={tr('settings.general.title')} subtitle={tr('settings.general.subtitle')} />
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 14 }}>
        <Field label={tr('settings.general.company')}>
          <Input value={companyName} onChange={e => setCompanyName(e.target.value)} dark={dark} />
        </Field>
        <Field label={tr('settings.general.timezone')}>
          <select
            aria-label={tr('settings.general.timezone')}
            value={timezone}
            onChange={e => setTimezone(e.target.value)}
            style={selectStyle(t)}
          >
            {tzOptions.map(tz => <option key={tz} value={tz}>{tz}</option>)}
          </select>
        </Field>
        <Field label={tr('settings.general.currency')}>
          <select
            aria-label={tr('settings.general.currency')}
            value={currency}
            onChange={e => setCurrency(e.target.value)}
            style={selectStyle(t)}
          >
            {CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
          </select>
        </Field>
      </div>
      <div style={{ marginTop: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
        <Button variant="primary" dark={dark} disabled={saving} onClick={() => void save()}>
          {tr('common.action.save_changes')}
        </Button>
        {saveState === 'success' && (
          <span style={{ fontSize: 12, color: '#027a48' }}>{tr('admin_settings.organisation.save_success')}</span>
        )}
        {saveState === 'error' && (
          <span style={{ fontSize: 12, color: '#b42318' }}>{tr('admin_settings.organisation.save_error')}</span>
        )}
      </div>
    </Card>
  );
}

/* ── Seite ─────────────────────────────────────────────────────────────────── */

/** Aktionsprotokoll als Sektion der Admin-Einstellungen (vormals eigene Seite /protokoll). */
function ProtokollSection({ dark }: { dark: boolean }) {
  const { t: tr } = useTranslation();
  return (
    <div style={{ minWidth: 0 }}>
      <SectionTitle
        dark={dark}
        title={tr('protokoll.page.title')}
        subtitle={tr('protokoll.page.subtitle')}
      />
      <ProtokollTable dark={dark} showFilters />
    </div>
  );
}

export function AdminEinstellungenPage({ dark, density }: AdminEinstellungenPageProps) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const d = DENSITY_VALUES[density];
  const isMobile = useIsMobile();
  const { hasPermission } = usePermissions();

  const [section, setSection] = useState<Section>('organisation');

  // Permission-Gate: ohne menu.admin_einstellungen kein Zugriff
  if (!hasPermission('menu.admin_einstellungen')) {
    return (
      <Card dark={dark} style={{ padding: d.cardPad, maxWidth: 520 }}>
        <SectionTitle
          dark={dark}
          title={tr('admin_settings.access_denied_title')}
          subtitle={tr('admin_settings.access_denied_text')}
        />
      </Card>
    );
  }

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : '240px 1fr',
      gap: isMobile ? 12 : d.gap,
      // Das Protokoll ist eine breite Tabelle; die übrigen Sektionen bleiben kompakt.
      maxWidth: section === 'protokoll' ? 1500 : 1100,
    }}>
      {/* Sektions-Nav (Desktop) / horizontale Tab-Leiste (Mobile) */}
      <Card
        dark={dark}
        style={isMobile ? {
          padding: 4,
          display: 'flex', flexDirection: 'row', gap: 6,
          overflowX: 'auto', WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'thin',
          position: 'sticky', top: 0, zIndex: 10,
        } : { padding: 12, alignSelf: 'start' }}
      >
        {SECTIONS.filter(s => !s.permission || hasPermission(s.permission)).map(s => {
          const Ic = Icon[s.ic];
          const active = section === s.k;
          return (
            <button
              key={s.k}
              onClick={() => setSection(s.k)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                width: isMobile ? 'auto' : '100%',
                padding: isMobile ? '9px 14px' : '9px 11px',
                borderRadius: 8, border: 'none',
                background: active ? BRAND_LIGHT : 'transparent',
                color: active ? BRAND : t.text,
                fontSize: 13, fontWeight: active ? 600 : 500, cursor: 'pointer',
                marginBottom: isMobile ? 0 : 2,
                flexShrink: 0, whiteSpace: 'nowrap',
                minHeight: isMobile ? 38 : undefined,
                textAlign: 'left', fontFamily: 'Outfit, sans-serif',
                transition: 'background 0.12s, color 0.12s',
              }}
            >
              <Ic c={active ? BRAND : t.muted} />{tr(s.labelKey)}
            </button>
          );
        })}
      </Card>

      {/* Inhalt */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: d.gap }}>
        {section === 'organisation' && (
          <OrganisationSection dark={dark} isMobile={isMobile} cardPad={d.cardPad} />
        )}
        {section === 'email' && <EmailTemplatesSettings dark={dark} />}
        {section === 'pb_stages' && <PhasenSettings dark={dark} />}
        {section === 'bk_stages' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <BkStagesSettings dark={dark} />
            <BkPauseSetting dark={dark} />
          </div>
        )}
        {section === 'abbruch_gruende' && <AbbruchGruendeSettings dark={dark} />}
        {section === 'matching' && <MatchingSettings dark={dark} />}
        {section === 'kalender' && <KalenderSettings dark={dark} />}
        {section === 'export' && <ExportSettings dark={dark} />}
        {section === 'docusign' && <DocusignSettings dark={dark} />}
        {section === 'api' && <ApiSettings dark={dark} />}
        {section === 'protokoll' && hasPermission('protokoll.read') && (
          <ProtokollSection dark={dark} />
        )}
        {section === 'rollen' && <RollenPage dark={dark} density={density} />}
        {/* Sonstiges — Leadquellen speisen das Quelle-Dropdown im
            Neue-Anfrage-Drawer (/anfragen). Batch-13 / Paket D:
            Wiedervorlage-Maximum darunter (eigenes Setting, eigener Save-Pfad). */}
        {section === 'sonstiges' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <LeadquellenSettings dark={dark} />
            <WiedervorlageMaxSetting dark={dark} />
          </div>
        )}
      </div>
    </div>
  );
}
